/* eslint-disable react/no-multi-comp */

import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import messages from './SimulatedAbilityModalMessages';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Modal,
  Select,
  TextArea,
  CreatablePaginatedSelect,
  PaginatedSelect,
  FakeInput,
} from 'sa-ui/';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as actions from './simulatedContentMapActions';
import queryString from 'query-string';
import debounce from 'debounce-promise';
import { api } from 'app/api';
import * as helpers from './simulatedContentMapHelpers';
import Field from 'app/common/Field';
import omit from 'lodash/omit';
import AbilityModalType from './simulatedAbilityModalType';

const fetchKeywordOptions = ({ q, page, idEvaluation, idCatalogingMatrix, idMatrixContent }) =>
  api
    .post(`/cataloging/getCatalogingKeyword.php?${queryString.stringify({ q, page })}`, {
      idEvaluation,
      idCatalogingMatrix,
      idMatrixContent,
      onlyAbility: true,
    })
    .then(response => response.data);

const fetchContentOptions = ({ q, page, idEvaluation, idCatalogingDiscipline }) =>
  api
    .post(
      `/evaluation-system/getCatalogingMatrixContents.php?${queryString.stringify({ q, page })}`,
      {
        idEvaluation,
        idCatalogingDiscipline,
      },
    )
    .then(response => response.data);

const formatGroupLabel = data => (
  <div>
    <strong>{data.label}</strong>
  </div>
);

const SimulatedAbilityModalForm = props => {
  function handleChangeIdCatalogingDiscipline(event) {
    props.getUnitsAbilityOptions({
      idEvaluation: props.idEvaluation,
      idAlternativeMatrix: props.idAlternativeMatrix,
      catalogingType: 'matrix',
      idCatalogingDiscipline: event.target.value,
    });
    props.setFieldValue(event.target.name, event.target.value);
    props.setFieldValue('idCatalogingMatrix', undefined);
    props.setFieldValue('contentOrIdMatrixContent', null);
    props.setFieldValue('cataloging', null);
  }

  function handleChangeIdCatalogingMatrix(event) {
    props.setFieldValue(event.target.name, event.target.value);
    props.setFieldValue('contentOrIdMatrixContent', null);
    props.setFieldValue('cataloging', null);
  }

  function handleClose() {
    props.resetForm({});
    props.onClose();
  }

  function mapValuesToPayload() {
    const data = {
      idEvaluation: props.idEvaluation,
      idCataloging: props.values.cataloging ? props.values.cataloging.value : undefined,
      ...props.values,
    };
    if (Number(props.values.contentOrIdMatrixContent.value)) {
      data.idMatrixContent = data.idMatrixContent || props.values.contentOrIdMatrixContent.value;
    } else {
      data.content = props.values.contentOrIdMatrixContent.value;
    }
    return omit(data, ['contentOrIdMatrixContent', 'cataloging']);
  }

  function getAction() {
    switch (props.type) {
      case AbilityModalType.ADD:
        return props.onAdd;
      case AbilityModalType.EDIT:
        return props.onEdit;
      case AbilityModalType.APPROVE:
        return props.onApprove;
      case AbilityModalType.REPROVE:
        return props.onReprove;
      default:
        return undefined;
    }
  }

  async function handleContinue() {
    const payload = mapValuesToPayload();
    const action = getAction();
    await action(payload);
    props.resetForm({});
  }

  const idMatrixContent =
    props.values.contentOrIdMatrixContent && !props.values.contentOrIdMatrixContent.__isNew__
      ? props.values.contentOrIdMatrixContent.value
      : undefined;

  const loadKeywordOptions = async (q, prevOptions, { page }) => {
    try {
      const [data, dataNext] = await Promise.all([
        fetchKeywordOptions({
          q,
          page,
          idEvaluation: props.idEvaluation,
          idCatalogingMatrix: props.values.idCatalogingMatrix,
          idMatrixContent,
        }),
        fetchKeywordOptions({
          q,
          page: page + 1,
          idEvaluation: props.idEvaluation,
          idCatalogingMatrix: props.values.idCatalogingMatrix,
          idMatrixContent,
        }),
      ]);
      return {
        options: helpers.getKeywordGroupedOptions(data.results, props.intl, page),
        hasMore:
          Object.keys(data.results).filter(Number).length > 0 ||
          (dataNext.results.contents
            ? dataNext.results.contents[0].contentAbilities.length > 0
            : false),
        additional: {
          page: page + 1,
        },
      };
    } catch (err) {
      console.error(err);
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  const loadKeywordOptionsDebounced = debounce(loadKeywordOptions, 350);

  const loadContentOptions = async (q, prevOptions, { page }) => {
    // if (!q || q.length < 3) {
    //   return {
    //     options: [],
    //   };
    // }

    try {
      const [data, dataNext] = await Promise.all([
        fetchContentOptions({
          q,
          page,
          idEvaluation: props.idEvaluation,
          idCatalogingDiscipline: props.values.idCatalogingDiscipline,
        }),
        fetchContentOptions({
          q,
          page: page + 1,
          idEvaluation: props.idEvaluation,
          idCatalogingDiscipline: props.values.idCatalogingDiscipline,
        }),
      ]);

      return {
        options: helpers.getContentOptions(data.contents, page),
        hasMore:
          Object.keys(data.contents).filter(Number).length > 0 ||
          (dataNext.contents ? dataNext.contents.length > 0 : false),
        additional: {
          page: page + 1,
        },
      };

      // try {
      //   const data = await fetchContentOptions({
      //     q,
      //     idEvaluation: props.idEvaluation,
      //     idCatalogingDiscipline: props.values.idCatalogingDiscipline,
      //   });

      //   const options = helpers.getContentOptions(data.contents) || [];
      //   return {
      //     options,
      //     hasMore: false,
      //   };
    } catch (err) {
      console.error(err);
      return {
        options: [],
        hasMore: false,
      };
    }
  };

  const loadContentOptionsDebounced = debounce(loadContentOptions, 350);

  function getUnitName() {
    if (!props.values.idCatalogingMatrix) return '';
    const unit = props.unitsAbilityOptions.find(u => u.value === props.values.idCatalogingMatrix);
    return unit ? unit.label : '';
  }

  function getTitleMessage() {
    switch (props.type) {
      case AbilityModalType.ADD:
        return messages.addTitle;
      case AbilityModalType.EDIT:
        return messages.editTitle;
      case AbilityModalType.APPROVE:
        return messages.approveTitle;
      case AbilityModalType.REPROVE:
        return messages.reproveTitle;
      case AbilityModalType.VIEW:
        return messages.viewTitle;
      default:
        return undefined;
    }
  }

  function getActionButtons() {
    const cancelButton = {
      label: <FormattedMessage {...messages.cancel} />,
      onClick: handleClose,
    };
    const closeButton = {
      label: <FormattedMessage {...messages.close} />,
      onClick: handleClose,
    };
    switch (props.type) {
      case AbilityModalType.VIEW:
        return [closeButton];
      case AbilityModalType.APPROVE:
        return [
          cancelButton,
          {
            label: <FormattedMessage {...messages.approve} />,
            onClick: handleContinue,
            className: 'text-success',
            primary: true,
          },
        ];
      case AbilityModalType.REPROVE:
        return [
          cancelButton,
          {
            label: <FormattedMessage {...messages.reprove} />,
            onClick: handleContinue,
            className: 'text-danger',
            primary: true,
          },
        ];
      default:
        return [
          cancelButton,
          {
            label: <FormattedMessage {...messages.continue} />,
            onClick: handleContinue,
            disabled: Object.keys(props.errors).length > 0,
            primary: true,
          },
        ];
    }
  }

  return (
    <Modal
      show={props.show}
      onClose={handleClose}
      icon="pe-7s-plus"
      title={<FormattedMessage {...getTitleMessage()} />}
      buttons={getActionButtons()}
    >
      <React.Fragment>
        {/* <pre>{JSON.stringify(props.values, null, 2)}</pre>
        <pre>{JSON.stringify(props.errors, null, 2)}</pre>
        <pre>valido {props.isValid ? 'SIM' : 'NÃO'}</pre> */}
        <Row className="mt-3">
          <Col xs={12} sm={6}>
            <Field
              component={Select}
              readOnly={props.readOnly}
              name="idCatalogingDiscipline"
              placeholder={props.intl.formatMessage(messages.curricularComponent)}
              options={props.curricularAbilityOptions}
              onChange={handleChangeIdCatalogingDiscipline}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Field
              component={Select}
              readOnly={props.readOnly}
              disabled={!props.values.idCatalogingDiscipline}
              placeholder={props.intl.formatMessage(messages.unit)}
              options={props.unitsAbilityOptions}
              onChange={handleChangeIdCatalogingMatrix}
              name="idCatalogingMatrix"
            />
          </Col>
        </Row>
        <Row>
          {!props.readOnly && (
            <Col xs={12}>
              <Field
                component={CreatablePaginatedSelect}
                placeholder=""
                key={props.values.idCatalogingMatrix}
                readOnly={props.readOnly}
                name="contentOrIdMatrixContent"
                isDisabled={!props.values.idCatalogingMatrix}
                label={<FormattedMessage {...messages.content} />}
                containerClassName="text-left"
                filterOption={() => true}
                loadOptions={loadContentOptionsDebounced}
                noOptionsMessage={() => props.intl.formatMessage(messages.noOptions)}
                loadingMessage={() => props.intl.formatMessage(messages.loading)}
                // formatCreateLabel={inputValue => inputValue}
                formatCreateLabel={inputValue => (
                  <span>
                    {props.intl.formatMessage(messages.createNew)} <strong>{inputValue}</strong>
                  </span>
                )}
                createOptionPosition="first"
                additional={{ page: 1 }}
                title={
                  props.values.contentOrIdMatrixContent
                    ? props.values.contentOrIdMatrixContent.label
                    : ''
                }
              />
            </Col>
          )}
          {props.readOnly && (
            <Col xs={12}>
              <FakeInput className="text-left" label={<FormattedMessage {...messages.content} />}>
                {props.values.contentOrIdMatrixContent
                  ? props.values.contentOrIdMatrixContent.label
                  : ''}
              </FakeInput>
            </Col>
          )}
          {!props.readOnly && (
            <Col xs={12}>
              <Field
                component={PaginatedSelect}
                name="cataloging"
                key={
                  props.values.contentOrIdMatrixContent
                    ? props.values.contentOrIdMatrixContent.label
                    : 'cataloging'
                }
                readOnly={props.readOnly}
                isDisabled={!props.values.idCatalogingMatrix}
                loadOptions={loadKeywordOptionsDebounced}
                label={
                  <FormattedMessage {...messages.abilitySelected}>
                    {txt => `${txt} ${getUnitName()}`}
                  </FormattedMessage>
                }
                // cacheUniq={{
                //   idEvaluation: props.idEvaluation,
                //   idCatalogingMatrix: props.values.idCatalogingMatrix,
                //   idMatrixContent,
                // }}
                placeholder=""
                containerClassName="text-left"
                nullable
                filterOption={() => true}
                formatGroupLabel={formatGroupLabel}
                noOptionsMessage={() => props.intl.formatMessage(messages.noOptions)}
                loadingMessage={() => props.intl.formatMessage(messages.loading)}
                formatCreateLabel={inputValue =>
                  `${props.intl.formatMessage(messages.createNew)} ${inputValue}`
                }
                styles={{
                  option: (styles, { data }) => ({
                    ...styles,
                    background: data.related ? '#e2f7e2' : undefined,
                  }),
                }}
                additional={{ page: 1 }}
                title={props.values.cataloging ? props.values.cataloging.label : ''}
              />
            </Col>
          )}
          {props.readOnly && (
            <Col xs={12}>
              <FakeInput
                className="text-left"
                label={
                  <FormattedMessage {...messages.abilitySelected}>
                    {txt => `${txt} ${getUnitName()}`}
                  </FormattedMessage>
                }
              >
                {props.values.cataloging ? props.values.cataloging.label : ''}
              </FakeInput>
            </Col>
          )}
          <Col xs={12}>
            <Field
              readOnly={props.readOnly}
              name="catalogingName"
              label={<FormattedMessage {...messages.indicatorInstituteAbility} />}
              containerClassName="text-left"
            />
          </Col>
          <Col xs={12}>
            <Field
              readOnly={props.readOnly}
              component={TextArea}
              name="catalogingDescription"
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FormattedMessage {...messages.comments} />
                  <OverlayTrigger
                    placement="right-start"
                    overlay={
                      <Tooltip>
                        Adicione neste campo informações pertinentes para a elaboração da prova,
                        indicando especificidades como por exemplo: Se escola selecionou uma
                        temática de <strong>Gramática</strong>, é importante especificar a classe
                        gramatical:{' '}
                        <strong>Com essa turma trabalhamos apenas com substantivos</strong>
                      </Tooltip>
                    }
                  >
                    <i className="material-icons ml-1">info</i>
                  </OverlayTrigger>
                </div>
              }
              helperText={props.intl.formatMessage(messages.commentsHelperText)}
              containerClassName="text-left"
            />
          </Col>
        </Row>
      </React.Fragment>
    </Modal>
  );
};

SimulatedAbilityModalForm.propTypes = {};

SimulatedAbilityModalForm.defaultProps = {
  type: AbilityModalType.ADD,
};

const mapStateToProps = ({ simulatedContentMap }) => ({
  ...simulatedContentMap,
});

export default compose(
  injectIntl,
  connect(
    mapStateToProps,
    { ...actions },
  ),
)(SimulatedAbilityModalForm);
